import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Card } from 'ui-components/components/card';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import HelpArticleLink from '../../../../../../components/help-article-link';
import { STEPS } from '../index';
import { messages } from './messages';
import style from './style.module.less';
import { linkIDs } from '../../../../../../services/link-service/links';

/**
 * @typedef {import('react-intl').MessageDescriptor} MessageDescriptor
 */

/**
 * @typedef {import('ui-components/components/checkbox').CheckboxChangeHandler} CheckboxChangeHandler
 */

/**
 * @typedef {object} StepData
 * @property {number} stepNumber
 * @property {MessageDescriptor} titleMessage
 * @property {MessageDescriptor} descriptionMessage
 * @property {MessageDescriptor} linkMessage
 * @property {MessageDescriptor} checkboxMessage
 * @property {string} articleId
 * @property {boolean} completed
 * @property {CheckboxChangeHandler} onChange
 */

/**
 * @param {object} props
 * @param {StepData} props.step
 * @returns {React.JSX.Element}
 */
const PrepareProductStep = ({ step }) => {
  const intl = useIntl();

  return (
    <div className={style.stepCard}>
      <div className={style.stepHeader}>
        <Text textType={TextType.H3} value={<FormattedMessage {...step.titleMessage} values={{ stepNumber: step.stepNumber }} />} />
      </div>
      <FlexColumn className={style.stepContent}>
        <Text className={style.stepDescription} textType={TextType.Paragraph} value={<FormattedMessage {...step.descriptionMessage} />} />
        <FlexRow className={style.linkContainer}>
          <HelpArticleLink articleId={step.articleId} className={style.linkButton}>
            <FormattedMessage {...step.linkMessage} />
          </HelpArticleLink>
        </FlexRow>
        <div className={style.checkboxContainer}>
          <Checkbox checked={step.completed} label={intl.formatMessage(step.checkboxMessage)} onChange={step.onChange} />
        </div>
      </FlexColumn>
    </div>
  );
};

/**
 * @param {import('../../types').BodyProps<import('../../types').PrepareProductsData>} props
 * @returns {React.JSX.Element}
 */
export const Body = ({ updateWizardStep, preparePackingListCheck, attachBarcodesToProductsCheck, finalizeProductListCheck }) => {
  /** @type {(stepId: keyof import('../../types').PrepareProductsData) => CheckboxChangeHandler} */
  const handleStepChange = (stepId) => (isChecked) => {
    updateWizardStep(
      {
        finalizeProductListCheck,
        attachBarcodesToProductsCheck,
        preparePackingListCheck,
        [stepId]: isChecked,
      },
      STEPS.prepareProducts,
    );
  };

  /** @type {StepData[]} */
  const steps = [
    {
      stepNumber: 1,
      titleMessage: messages.finalizeProductsListTitle,
      descriptionMessage: messages.finalizeProductsListDescription,
      linkMessage: messages.downloadCsvTemplate,
      checkboxMessage: messages.finalizeProductsListCheckbox,
      articleId: linkIDs.ImportCsvProducts,
      completed: finalizeProductListCheck,
      onChange: handleStepChange('finalizeProductListCheck'),
    },
    {
      stepNumber: 2,
      titleMessage: messages.attachBarcodesTitle,
      descriptionMessage: messages.attachBarcodesDescription,
      linkMessage: messages.learnMoreBarcodeRequirements,
      checkboxMessage: messages.attachBarcodesCheckbox,
      articleId: linkIDs.FulfillmentProviders,
      completed: attachBarcodesToProductsCheck,
      onChange: handleStepChange('attachBarcodesToProductsCheck'),
    },
    {
      stepNumber: 3,
      titleMessage: messages.preparePackingListTitle,
      descriptionMessage: messages.preparePackingListDescription,
      linkMessage: messages.learnMorePackingListRequirements,
      checkboxMessage: messages.preparePackingListCheckbox,
      articleId: linkIDs.CustomizePackingSlips,
      completed: preparePackingListCheck,
      onChange: handleStepChange('preparePackingListCheck'),
    },
  ];

  return (
    <FlexColumn className={style.prepareProductsBody}>
      <Card>
        {steps.map((step) => (
          <PrepareProductStep key={step.stepNumber} step={step} />
        ))}
      </Card>
    </FlexColumn>
  );
};
